import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema, textAreaInputSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../constants/errors';
import { ZodError } from 'zod';
import { ErrorSummary } from '@atoms/*';
import { TEXT_AREA_MAX_LENGTH } from '@constants';
import {
  FormTitle,
  NavigationButtons,
  RadioOptions,
  TextArea,
} from '@molecules/*';

export const ShortTermLetsAd: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, update, resetSelective } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Is the property being advertised as a short-term let?';
  const propertyDetailsLabelText = 'Give more details ';
  const propertyDetailsDescription =
    'For example, a link to an advert on sites like Airbnb.';

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: data.shortTermLetsAd === 'Yes',
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: data.shortTermLetsAd === 'No',
    },
    {
      id: 'DoNotKnow',
      title: 'DoNotKnow',
      labelText: 'I do not know',
      isDefault: data.shortTermLetsAd === 'DoNotKnow',
    },
  ];

  const handleChangeOptions = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No' | 'DoNotKnow';

    if (updatedSelection) {
      update('shortTermLetsAd', updatedSelection);
    } else {
      resetSelective();
    }
  };

  const handleChangePropertyDetails = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    update('detailsOfShortTermLetsAd', event.currentTarget.value);
    setErrorMessages([]);
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.OWNER_LIVE_AT_PROP);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'shortTermLetsAd',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          shortTermLetsAd: data.shortTermLetsAd,
        });
        if (data.shortTermLetsAd === 'Yes') {
          textAreaInputSchema({
            fieldName: 'detailsOfShortTermLetsAd',
            requiredMessage:
              'Enter details about the short-term let advertisement.',
            maxLength: TEXT_AREA_MAX_LENGTH,
            maxLengthMessage: `Details about the advertisement must be ${TEXT_AREA_MAX_LENGTH} characters or less.`,
          }).parse({
            detailsOfShortTermLetsAd: data.detailsOfShortTermLetsAd,
          });
        }
        navigate(RoutesList.SHORT_TERM_LETS_PROBLEM);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>
      <FormTitle text={title} />
      <RadioOptions
        options={options}
        onChange={handleChangeOptions}
        errorPath={'shortTermLetsAd'}
        errorMessages={errorMessages}
      />
      {data.shortTermLetsAd === 'Yes' ? (
        <TextArea
          labelText={propertyDetailsLabelText}
          isLabelTextTitle={false}
          description={propertyDetailsDescription}
          value={data.detailsOfShortTermLetsAd}
          onChange={handleChangePropertyDetails}
          errorPath={'detailsOfShortTermLetsAd'}
          errorMessages={errorMessages}
        />
      ) : null}
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
